import { Divider, IconButton, List, ListItem, ListItemText, ListSubheader, makeStyles, Tooltip, Typography } from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import SettingsIcon from "@material-ui/icons/Settings";
import classNames from "classnames";
import React from "react";
import { TelemetryService } from "../../../../src/services/TelemetryService";
import { EventTracking } from "../../../../src/utilities/Tracking/EventTracking";
import BackgroundImages from "../../../resources/BackgroundImages.json";
import { BuilderAssetService } from "../../../services/BuilderAssetService";
import { ServerResponse } from "../../../services/server/OldWebClient";
import { useEditor } from "../editor/useEditor";
import { ExportSlidesModal } from "./components/exportSlidesModal";
import { useBuilder } from "../useBuilder";
import { useBuilderDrawer } from "./useBuilderDrawer";
import { SlideSettingsDialog } from "./components/slideSettingsDialog";
import { AspectRatios } from "../../../entities/AspectRatios";
import { BuilderPreferences } from "../../../entities/BuilderPreferences";
import { BuilderAsset } from "../../../entities/BuilderAsset";
import { DisplaySlide } from "../editor/DisplaySlide";
import { exportSlides } from "../../../utilities/ExportSlides";

const useStyles = makeStyles((theme) => ({
	toolbar: theme.mixins.toolbar,
	title: {
		paddingTop: 10,
		paddingBottom: 7,
		textAlign: "left",
		fontSize: 22,
	},
	subtitle: {
		paddingTop: theme.spacing(0),
		textAlign: "left",
		fontSize: 15,
		textOverflow: "ellipsis",
	},
	closeIcon: {
		float: "right",
	},
	list: {
		marginBottom: "4px",
		position: "relative",
		overflowX: "hidden",
	},
	placeHolderTextContainer: {
		background: "#efefef",
		height: "100%",
		padding: "17px",
	},
	placeHolderText: {
		fontFamily: "Roboto, Helvetica",
		fontSize: "15px",
		color: "#5f6368",
		paddingTop: "17px",
	},
	settingsIcon: {
		float: "right",
		"&:hover": {
			backgroundColor: "rgb(145, 145, 145)",
		},
	},
	bottomBtnList: {
		marginTop: "auto",
		display: "flex",
		[theme.breakpoints.down("xs")]: {
			paddingBottom: "20px",
		},
	},
	bottomBtnListItem: {
		textAlign: "center",
	},
	noPadding: {
		paddingRight: "0px !important",
	},
}));

interface BackgroundImage {
	id: number;
	standardImagePath: string;
	standardImageThumbnailPath: string;
	widescreenImagePath: string;
	widescreenImageThumbnailPath: string;
	name: string;
	fontColor: string;
}

const images: BackgroundImage[] = BackgroundImages as any;
const currentLocation = "Slide Drawer Interaction";

export const slideAspectRatios = [
	{
		id: "16x9",
		name: "16 x 9",
	},
	{
		id: "4x3",
		name: "4 x 3",
	},
];

export function SlidesContent() {
	const [exportModalOpen, setExportModalOpen] = React.useState(false);
	const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false);
	const classes = useStyles()
	const editor = useEditor();
	const builder = useBuilder();
	const builderDrawer = useBuilderDrawer();

	const exportToPresentation = async () => {
		exportSlides(builder.builderAsset, editor.slides, builder.preferences);
		let now = new Date();
		TelemetryService.updateContactField("sc_cloud_slideexport", now.toString());
		EventTracking.Builder.onExportSlides(currentLocation);
	};

	const handlePresentClick = () => {
		EventTracking.Builder.onPresentSlides(currentLocation);
		setExportModalOpen(true);
	};

	const getImageThumbnail = () => {
		const customImageBase64Content = builder.customImageBase64Content;
		const { customSlideBackgroundImageUrl, slideExporterAspectRatio } = builder.builderAsset;

		if (customImageBase64Content) {
			return customImageBase64Content;
		} else if (customSlideBackgroundImageUrl) {
			return customSlideBackgroundImageUrl;
		} else {
			const backgroundImage = getStockBackgroundImage();
			if (slideExporterAspectRatio === "4x3") {
				return backgroundImage.standardImageThumbnailPath;
			}

			return backgroundImage.widescreenImageThumbnailPath;
		}
	};

	const getStockBackgroundImage = () => {
		const { slideExporterSelectedGalleryImageId } = builder.preferences;
		const selectedImage = images.find((image) => image.id === slideExporterSelectedGalleryImageId);
		return selectedImage ? selectedImage : images[0];
	};

	const getFontColor = () => {
		const { builderAsset, customImageBase64Content, preferences } = builder;
		if (builderAsset.customSlideBackgroundImageUrl || customImageBase64Content) {
			return "#FFFFFF";
		}

		const selectedImage = images.find((image) => image.id === preferences.slideExporterSelectedGalleryImageId);
		const backgroundImage = selectedImage ? selectedImage : images[0];

		return backgroundImage.fontColor;
	};

	const onClose = () => {
		EventTracking.Builder.onCloseMainDrawerFromX();
		builderDrawer.onMainDrawerChange(false);
	};

	const onSettingsDialogToggle = () => {
		setSettingsDialogOpen(!settingsDialogOpen);
		if (!settingsDialogOpen) {
			EventTracking.Builder.onOpenSettings(currentLocation);
		}
	};

	const saveSlidesSettings = async (preferredAspectRatio: AspectRatios, preferredImageId: number, uploadedCustomImage: boolean) => {
		setSettingsDialogOpen(false);
		let preferences: BuilderPreferences = {
			...builder.preferences,
			slideExporterSelectedGalleryImageId: preferredImageId,
		};

		builder.savePreferences(preferences);

		const asset = builder.builderAsset;

		if (preferredAspectRatio !== asset.slideExporterAspectRatio) {
			if (preferredAspectRatio === "16x9") {
				EventTracking.Builder.onPreferencesChanges("Slides", "Aspect Ratio - 16x9");
			} else {
				EventTracking.Builder.onPreferencesChanges("Slides", "Aspect Ratio - 4x3");
			}
		}

		if (hasUploadedSlideImage) {
			EventTracking.Builder.onPreferencesChanges("Slides", "Image Uploaded");
		}

		let response = await BuilderAssetService.saveSlideAspectRatio(asset.id, preferredAspectRatio);
		if (ServerResponse.isSuccess<BuilderAsset>(response)) {
			builder.setBuilderAsset({
				...asset,
				slideExporterAspectRatio: preferredAspectRatio,
			});
		} else {
			builder.setAlert("Failed to update slide preferences.");
		}
	};

	const onDeleteCustomBackgroundImage = () => {
		builder.setBuilderAsset({
			...builder.builderAsset,
			customSlideBackgroundImagePath: undefined,
			customSlideBackgroundImageUrl: undefined,
		});
		builder.setCustomImageBase64Content("");
	};

	const onSlidePreferenceChange = (asset: BuilderAsset, customImageAddedClient64Base: string, customImageAddedClientMimeType: string) => {
		builder.setBuilderAsset({
			...builder.builderAsset,
			slideExporterAspectRatio: asset.slideExporterAspectRatio,
			customSlideBackgroundImagePath: asset.customSlideBackgroundImagePath,
		});

		builder.setCustomImageBase64Content(customImageAddedClient64Base);
	};

	const { slideExporterSelectedGalleryImageId } = builder.preferences;
	const hasUploadedSlideImage = builder.builderAsset.customSlideBackgroundImagePath ? true : undefined;
	let preferredAspectRatio = builder.builderAsset.slideExporterAspectRatio;
	if (!preferredAspectRatio) {
		preferredAspectRatio = "16x9";
	}
	return (
		<>
			<React.Fragment>
				<ListSubheader className={classes.title}>
					Slide Preview
					<IconButton onClick={onClose} className={classes.closeIcon}>
						<CloseIcon />
					</IconButton>
				</ListSubheader>
				<Divider />
				<ListSubheader className={classes.subtitle} color="primary">
					<span style={{ cursor: "pointer" }} onClick={() => onSettingsDialogToggle()}>
						Presentation Settings
					</span>
					<Tooltip title="Change Settings">
						<IconButton onClick={() => onSettingsDialogToggle()} className={classes.settingsIcon}>
							<SettingsIcon />
						</IconButton>
					</Tooltip>
				</ListSubheader>
				<Divider />
				{editor.slides.length > 0 && (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							overflow: "hidden",
						}}
					>
						<List className={classNames("customScrollbar", classes.list)}>
							{editor.slides.map((slide, index) => {
								return (
									<DisplaySlide
										key={index}
										onRemovePreviewSlide={editor.onRemovePreviewSlide}
										slideExporterAspectRatio={builder.builderAsset.slideExporterAspectRatio}
										getImageThumbnail={getImageThumbnail}
										getFontColor={getFontColor}
										onSettingsDialogToggle={onSettingsDialogToggle}
										previewSlide={slide}
										index={index}
									/>
								);
							})}
						</List>
						<List className={classes.bottomBtnList}>
							<ListItem button onClick={exportToPresentation} className={classes.bottomBtnListItem}>
								<ListItemText primary="Download" className={classes.noPadding} />
							</ListItem>
							<ListItem button onClick={handlePresentClick} className={classes.bottomBtnListItem}>
								<ListItemText primary="Present" className={classes.noPadding} />
							</ListItem>
							<ListItem button onClick={() => builder.showShareDialog(true, currentLocation)} className={classes.bottomBtnListItem}>
								<ListItemText primary="Share" className={classes.noPadding} />
							</ListItem>
						</List>
					</div>
				)}
				{editor.slides.length === 0 && (
					<div className={classes.placeHolderTextContainer}>
						<Typography className={classes.placeHolderText}>Building a slide deck from your sermon has never been easier!</Typography>
						<Typography className={classes.placeHolderText}>
							In the sermon editor, highlight any section and click <strong>ADD SLIDE</strong> in the bubble appearing above the text.
						</Typography>
						<Typography className={classes.placeHolderText}>
							After adding slides, click the gear icon above to change the background image and aspect ratio.
						</Typography>
						<Typography className={classes.placeHolderText}>
							Once your slide deck is ready, click the blue <strong>Download</strong> button below to download your file.
						</Typography>
						<Typography className={classes.placeHolderText}>
							Alternatively, click the blue <strong>Present</strong> button below to export your presentation to Google Slides (Note:
							Google account required).
						</Typography>
						<Typography className={classes.placeHolderText}>
							Need more help?{" "}
							<a href="https://www.sermonmaker.net/help/adding-slides-to-your-sermon/" target="_blank" rel="noopener noreferrer">
								Learn more
							</a>
						</Typography>
					</div>
				)}
				{exportModalOpen && (
					<ExportSlidesModal
						config={builder.appConfig.googleSlidesApi}
						open={exportModalOpen}
						handleClose={() => setExportModalOpen(false)}
						assetId={builder.builderAsset.id}
						hasCustomImage={builder.builderAsset.customSlideBackgroundImagePath != null}
						defaultImageId={slideExporterSelectedGalleryImageId!}
						imageAspectRatio={preferredAspectRatio}
						customSlideBackgroundImagePath={builder.builderAsset.customSlideBackgroundImagePath}
					/>
				)}
				<SlideSettingsDialog
					id={builder.builderAsset.id}
					key={`${builder.builderAsset.slideExporterAspectRatio}${slideExporterSelectedGalleryImageId}${hasUploadedSlideImage}`}
					open={settingsDialogOpen}
					onClose={() => setSettingsDialogOpen(false)}
					onSave={saveSlidesSettings}
					preferredImageId={slideExporterSelectedGalleryImageId!}
					preferredAspectRatio={builder.builderAsset.slideExporterAspectRatio ? builder.builderAsset.slideExporterAspectRatio : "16x9"}
					backgroundImageAspectRatio={builder.builderAsset.slideExporterAspectRatio}
					onDeleteCustomSave={onDeleteCustomBackgroundImage}
					onCustomBackgroundSave={onSlidePreferenceChange}
					hasPreferredCustomImage={hasUploadedSlideImage !== undefined}
					customSlideBackgroundImagePath={builder.builderAsset.customSlideBackgroundImageUrl}
					customImageBase64Content={builder.customImageBase64Content}
				/>
			</React.Fragment>
		</>
	);
}